import { useState, useEffect } from "react";
import { useHeaderContext } from "../../../../context/headerContext";
import { useLocation, useParams } from "react-router-dom";
import Button from "../../../../components/Button";
import Lock from "/public/images/icons/lockedBand.svg?react";
import Unlock from "/public/images/icons/unlockBand.svg?react";
import { parseISO, differenceInSeconds, differenceInMinutes, differenceInHours, differenceInDays } from 'date-fns';
import { useDrawerContext } from "../../../../context/drawerContext";
import CloseIcon from "/public/images/icons/close.svg?react";
import { useUserContext } from "../../../../context/userContext";
import fetchWithRefresh from "../../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../../utils/LogErrors";

export default function BandPage() {
  const location = useLocation();
  const { user, logout, updateTokens } = useUserContext();
  const { setTitle, setBackButton } = useHeaderContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const { bandId } = useParams();
  const { band, participant } = location.state || {};
  const [bandData, setBandData] = useState(null);
  const [lastUpdatedText, setLastUpdatedText] = useState("Unknown time");
  const [refreshDate, setRefreshDate] = useState(false);
  const [triggerBandFound, setTriggerBandFound] = useState(false);

  useEffect(() => {
    fetchBand();
    setTitle(`${band?.FriendlyName || participant?.Nickname || participant?.ParticipantName}`);
    setBackButton(true);
  }, [band]);

  useEffect(() => {
    if(bandData){
      setRefreshDate(true);
    }
  }, [bandData])

  const fetchBand = async () => {
    await Sentry.startSpan({ name: "Fetch Band", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Bands/FetchBand?bandid=${band?.BandID || bandId}`;
        span.setAttribute("http.url", url);
  
        const response = await fetchWithRefresh(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        span.setAttribute("http.status_code", response.status);
  
        if (response.ok) {
          const data = await response.json();
          setBandData(data);
          setRefreshDate(true);
          setOpen(false);
        } else {
          setRefreshDate(false);
          Sentry.captureMessage("Failed to fetch band", { extra: { status: response.status, url } });
          throw new Error("Failed to fetch band");
        }
      } catch (error) {
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, error);
        span.setStatus("error");
      } finally {
        span.end();
      }
    });
  };

  const unlockBand = async () => {
    setRefreshDate(false);
    try {
      const url = `${import.meta.env.VITE_API_URL}Bands/UnlockBand?bandId=${band?.BandID || bandId}&userTriggering=${user.id}`;
      const response = await fetchWithRefresh(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      }, logout, updateTokens, (message) => { toast.info(message); });
  
      if (response.ok) {
        fetchBand();
      } else {
        const error = await response.text();
        setRefreshDate(true);
        Sentry.captureMessage("Failed to unlock band", { extra: { status: response.status, url, error } });
        throw new Error("Failed to unlock band");
      }
    } catch (error) {
      Sentry.captureException(error);
      LogErrors(user, logout, updateTokens, error);
    }
  };

  const lockBand = async () => {
    setRefreshDate(false);
    try {
      const url = `${import.meta.env.VITE_API_URL}Bands/LockBand?bandId=${band?.BandID || bandId}&userTriggering=${user.id}`;
      const response = await fetchWithRefresh(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      }, logout, updateTokens, (message) => { toast.info(message); });
  
      if (response.ok) {
        fetchBand();
      } else {
        Sentry.captureMessage("Failed to lock band", { extra: { status: response.status, url } });
        throw new Error("Failed to lock band");
      }
    } catch (error) {
      Sentry.captureException(error);
      LogErrors(user, logout, updateTokens, error);
    }
  };

  const lostBand = async () => {
    setRefreshDate(false);
    try {
      const url = `${import.meta.env.VITE_API_URL}Bands/LostBand?bandId=${band?.BandID}&userTriggering=${user.id}`;
      const response = await fetchWithRefresh(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      }, logout, updateTokens, (message) => { toast.info(message); });
  
      if (response.ok) {
        fetchBand();
        temporaryDeactivationBandSuccess();
      } else {
        Sentry.captureMessage("Failed to mark band as lost", { extra: { status: response.status, url } });
        throw new Error("Failed to mark band as lost");
      }
    } catch (error) {
      Sentry.captureException(error);
      LogErrors(user, logout, updateTokens, error);
    }
  };

  const foundBand = async () => {
    setRefreshDate(false);
    try {
      const url = `${import.meta.env.VITE_API_URL}Bands/FoundBand?bandid=${band?.BandID}&userTriggering=${user.id}`;
      const response = await fetchWithRefresh(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      }, logout, updateTokens, (message) => { toast.info(message); });
  
      if (response.ok) {
        fetchBand();
        setTriggerBandFound(true);
      } else {
        Sentry.captureMessage("Failed to find band", { extra: { status: response.status, url } });
        throw new Error("Failed to find band");
      }
    } catch (error) {
      Sentry.captureException(error);
      LogErrors(user, logout, updateTokens, error);
    }
  };

  const permanentlyDeactivateBand = async () => {
    setRefreshDate(false);
    try {
      const url = `${import.meta.env.VITE_API_URL}Bands/PermanentlyDeactivateBand?bandId=${band?.BandID}&userTriggering=${user.id}`;
      const response = await fetchWithRefresh(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      }, logout, updateTokens, (message) => { toast.info(message); });
  
      if (response.ok) {
        permanentDeactivationBandSuccess();
      } else {
        Sentry.captureMessage("Failed to permanently deactivate band", { extra: { status: response.status, url } });
        throw new Error("Failed to permanently deactivate band");
      }
    } catch (error) {
      Sentry.captureException(error);
      LogErrors(user, logout, updateTokens, error);
    }
  };

  const bandLost = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            BAND LOST
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false)}} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Band Lost?</h3> 
          <p className="text-sportsDarkGrey font-poppins mb-[24px]">Temporarily deactivate your band so that the QR code on the band cannot be unlocked.</p>
          <div className="flex flex-col gap-y-[16px]">
            <Button variant="primary" onClick={() => lostBand()}>Temporarily Deactivate Band</Button>
            <Button variant="quaternary" onClick={() => setOpen(false)}>Keep Band</Button>
          </div>
        </div>
      </div>
    );
  };

  const bandFound = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            BAND FOUND
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false)}} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Band Found?</h3>
          <p className="text-sportsDarkGrey font-poppins mb-[24px]">Reactivate your band so that the QR code can be unlocked.</p>
          <div className="flex flex-col gap-y-[16px]">
            <Button variant="primary" onClick={() => foundBand()}>Reactivate Band</Button>
            <Button variant="quaternary" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      </div>
    );
  };

  const bandStolen = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            BAND STOLEN
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Band Stolen?</h3>
          <p className="text-sportsDarkGrey font-poppins mb-[24px]">Permanently deactivate your band so that the QR code on the band cannot be unlocked.</p>
          <div className="flex flex-col gap-y-[16px]">
            <Button variant="primary" onClick={() => deactivateBand()}>Permanently Deactivate Band</Button>
            <Button variant="quaternary" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      </div>
    );
  };

  const deactivateBand = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            DEACTIVATE BAND
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Permanently deactivate band, are you sure?</h3>
          <p className="text-sportsDarkGrey font-poppins mb-[24px]">All information will be deleted and the band will no longer work.</p>
          <div className="flex flex-col gap-y-[16px]">
            <Button variant="primary" onClick={() => permanentlyDeactivateBand()}>Deactivate Band and Order a New One</Button>
            <Button variant="quaternary" onClick={() => setOpen(false)}>Keep Band</Button>
          </div>
        </div>
      </div>
    );
  };

  const permanentDeactivationBandSuccess = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            DEACTIVATE BAND
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px] mb-[24px]">Band marked as deactivated successfully</h3>
          <Button variant="primary" onClick={() => setOpen(false)} href={`https://shop.sportsguardian.com/${bandData.OrgPartnerId}/getsafety`} target="_blank">Close</Button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const updateLastUpdatedText = () => {
      if (!bandData?.LastStateChangeDate) return;

      const lastUpdatedDate = parseISO(bandData.LastStateChangeDate);
      const now = new Date();
      const diffInSeconds = differenceInSeconds(now, lastUpdatedDate);
      let timeText = '';

      if (diffInSeconds < 60) {
        timeText = `${diffInSeconds} SEC${diffInSeconds !== 1 ? 'S' : ''} AGO`;
      } else if (diffInSeconds < 3600) {
        const diffInMinutes = differenceInMinutes(now, lastUpdatedDate);
        timeText = `${diffInMinutes} MIN${diffInMinutes !== 1 ? 'S' : ''} AGO`;
      } else if (diffInSeconds < 86400) {
        const diffInHours = differenceInHours(now, lastUpdatedDate);
        timeText = `${diffInHours} HOUR${diffInHours !== 1 ? 'S' : ''} AGO`;
      } else {
        const diffInDays = differenceInDays(now, lastUpdatedDate);
        timeText = `${diffInDays} DAY${diffInDays !== 1 ? 'S' : ''} AGO`;
      }

      setLastUpdatedText(timeText);
    };

    updateLastUpdatedText();
    const intervalId = setInterval(updateLastUpdatedText, 60000); // Update every minute

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [bandData]);

  const capitalize = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  };

  return (
    <div>
      {refreshDate && (
        <>
          <div className={`flex flex-col gap-y-[24px] ${bandData?.BandStateVerbose !== "NOT ACTIVATED" ? 'border-dashed border-b-[1px] border-[#E3E3E3] pb-[30px] mb-[30px]': ''}`}>
            {(bandData?.BandStateVerbose !== "NOT ACTIVATED" && bandData?.BandStateVerbose !== "PERMANENTLY DISABLED") && (
              <>
                {(bandData?.BandStateVerbose !== "CHECKED IN" && bandData?.BandStateVerbose !== "LOST") && (
                  <>
                  {bandData?.BandStateVerbose !== "LOCKED" ? (
                    <Button className="w-full" onClick={() => lockBand()}>
                      <Lock />
                      Lock Band
                    </Button>
                  ) : (
                    <Button className="w-full" onClick={() => unlockBand()}>
                      <Unlock />
                      Unlock Band
                    </Button>
                  )}
                  </>
                )}
                {bandData?.BandStateVerbose !== "LOST" ? (
                  <Button variant="quaternary" onClick={bandLost}>Band Lost</Button>
                ) : (
                  <Button variant="quaternary" onClick={bandFound}>Band Found</Button>
                )}
                <Button variant="tertiary" status="nobg-red" onClick={bandStolen}>Band Stolen?</Button>
              </>
            )}
          </div>
          <div>
            <div className="mb-6">
              {bandData?.BandStateVerbose !== "NOT ACTIVATED" && (
                <p className="text-[12px] leading-[24px] font-semibold tracking-[0.2em] mb-1">
                  {bandData?.BandStateVerbose} • {lastUpdatedText}
                </p>
              )}
              {bandData?.BandStateVerbose === "LOST" ? (
                <h2 className="mb-6">Your band has been locked and temporarily deactivated.</h2>
              ) : triggerBandFound ? (
                <h2 className="mb-6">Your band has been reactivated in the default Locked state.</h2>
              ) : bandData?.BandStateVerbose !== "NOT ACTIVATED" ? (
                <h2 className="mb-6">Your band is now <span>{capitalize(bandData?.BandStateVerbose)}</span></h2>
              ) : (
                <h2 className="mb-6">Your band has not been activated</h2>
              )}
              
              <p className="text-[16px] leading-[26px] text-sportsGrey">
                {(bandData?.BandStateVerbose === "CHECKED IN" || bandData?.BandStateVerbose === "UNLOCKED") ? (
                  'Your medical information, ICE contacts and other information are visible when the QR code is scanned.'
                ) : bandData?.BandStateVerbose !== "NOT ACTIVATED" ? (
                  'Your medical information, ICE contacts and other information are not visible when the QR code is scanned.'
                ) : (
                  'Your band has not been activated. Please select Activate Band from the Home screen.'
                )}
              </p>
            </div>
            <div className="max-w-[229px] mx-auto text-center">
              <img
                src={bandData?.BandImageURL}
                alt="band"
                width={229}
                height={191}
                className="mb-4"
              />
              <h4 className="text-sportsGrey mb-2">{bandData?.OrderItemParticipantName || participant?.ParticipantName}</h4>
              <h4 className="text-sportsGrey mb-2">ICE: <a href={`tel:${bandData?.OrderItemICENumber || participant?.PrimaryIceNumber}`}>{bandData?.OrderItemICENumber || participant?.PrimaryIceNumber}</a></h4>
              <h4 className="text-sportsGrey">{bandData?.OrderItemMedical || participant?.PrimaryMedicalCondition}</h4>

            </div>
          </div>
        </>
      )}
    </div>
  );
}